import React, { HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import store from "store";
import cx from "classnames";
import { Hooks } from "@xt/xtbase";
const { useTranslation } = Hooks;
import Storage from "utils/storage";
import tabCfgAry from "./tabs/tabCfgAry";

// import store from "store";

import XtInputSearch from "components/xt/input/search";
import CMPT_Tabs from "./tabs";
import CMPT_Sort from "./sort";
import CMPT_Container from "./container";
import CMPT_TopSearch from "./topSearch";

import styles from "./index.module.scss";

import { TabCfgProps } from "./tabs";

interface Props extends HTMLAttributes<HTMLDivElement> {
  clsSearch?: string;
  /** 选中任意交易对时回调（用于 CFD 等场景下关闭弹框，包括选中当前 pair） */
  onMarketSelect?: () => void;
}

const Main: React.FC<Props> = ({ className, clsSearch, onMarketSelect }) => {
  const t = useTranslation();
  const router = useRouter();

  // 检测当前是否在 CFD 页面
  const isCfd = useMemo(() => {
    return router.pathname?.startsWith("/cfd/") || router.asPath?.startsWith("/cfd/");
  }, [router.pathname, router.asPath]);

  // 检测当前是否是法币本位合约
  const isFiatContract = useMemo(() => {
    if (!isCfd) return false;
    const { name } = store.market;
    if (!name) return false;
    const quoteCurrency = name.split("_")[1]?.toUpperCase();
    const fiatCurrencies = ["AED", "BRL", "SAR"];
    return fiatCurrencies.includes(quoteCurrency);
  }, [isCfd, store.market.name]);

  const [keyword, seKeyword] = useState<string>("");
  const [isSearchInputFocus, setIsSearchInputFocus] = useState<WithUndefined<boolean>>(undefined);
  const isTopSearchVisible = useMemo(() => {
    return !(keyword || !isSearchInputFocus);
  }, [keyword, isSearchInputFocus]);

  const inputRef = useRef<HTMLInputElement>(null);
  const refIsFocus = useRef(false);
  const handleSearchInputFocus = useCallback(() => {
    console.log("onFocus");
    setIsSearchInputFocus(true);
  }, []);
  const handleSearchInputBlur = useCallback(() => {
    console.log("onBlur");
    if (!refIsFocus.current || !inputRef.current) {
      setIsSearchInputFocus(false);
    } else {
      refIsFocus.current = false;
      inputRef.current.focus();
    }
  }, []);
  const setSearchInputFocus = useCallback(() => {
    refIsFocus.current = true;
  }, []);

  const [tabCfg, setTabCfg] = useState<TabCfgProps>(tabCfgAry[1]);
  // 法币合约下唯一的法币 tab（合并 AED/BRL/SAR 为一个 Crypto tab）
  const firstFiatTab = useMemo(
    () => (isCfd && isFiatContract ? tabCfgAry.find((item) => item.key === "fiat") ?? null : null),
    [isCfd, isFiatContract]
  );

  useEffect(() => {
    const tabCfg = Storage.get("marketGroup");

    if (tabCfg && tabCfg.key) {
      // 如果在现货页面但选中的是法币 tab (fiat)，重置为默认 tab
      if (!isCfd && tabCfg.key === "fiat") {
        setTabCfg(tabCfgAry[1]);
        Storage.set("marketGroup", tabCfgAry[1]);
        return;
      }
      // 如果是 U 本位合约但选中的是法币 tab，重置为默认 tab
      if (isCfd && !isFiatContract && tabCfg.key === "fiat") {
        setTabCfg(tabCfgAry[1]);
        Storage.set("marketGroup", tabCfgAry[1]);
        return;
      }
      // 法币本位合约：选中的不是法币 tab（且非 user/commodities/stocks）→ 选中法币 tab
      if (isCfd && isFiatContract && firstFiatTab) {
        const notFiatTab = tabCfg.key !== "fiat";
        const isCommoditiesOrStocks = tabCfg.key === "commodities" || tabCfg.key === "stocks";
        if (notFiatTab && tabCfg.key !== "user" && !isCommoditiesOrStocks) {
          setTabCfg(firstFiatTab);
          Storage.set("marketGroup", firstFiatTab);
          return;
        }
      }
      if (tabCfg.key !== "user" || store.user.isLogin) setTabCfg(tabCfg);
    } else {
      // 首次打开弹窗、未选择过 tab 时无 storage：按当前页面类型选中第一个 tab
      if (isCfd && isFiatContract && firstFiatTab) {
        setTabCfg(firstFiatTab);
        Storage.set("marketGroup", firstFiatTab);
      } else if (!isCfd || !isFiatContract) {
        setTabCfg(tabCfgAry[1]);
        Storage.set("marketGroup", tabCfgAry[1]);
      }
    }
  }, [isCfd, isFiatContract, firstFiatTab]);
  const onTabChange = (tabCfg) => {
    setTabCfg(tabCfg);
    // console.log(1111111, tabCfg);
    Storage.set("marketGroup", tabCfg);
  };

  const [sortBy, setSortBy] = useState<string | undefined>();
  const [isShowVolume, setIsShowVolume] = useState(false);

  return (
    <div className={cx(styles.main, className)}>
      <div className={styles.search}>
        <XtInputSearch
          innerRef={inputRef}
          className={clsSearch}
          placeholder={t("trade.search2")}
          value={keyword}
          onInput={(val) => seKeyword(val)}
          onFocus={handleSearchInputFocus}
          onBlur={handleSearchInputBlur}
        />
      </div>

      {isSearchInputFocus !== undefined && (
        <CMPT_TopSearch className={styles.topSearch} visible={isTopSearchVisible} setSearchInputFocus={setSearchInputFocus} isShowVolume={isShowVolume} />
      )}

      <div className={styles.content}>
        {!keyword && <CMPT_Tabs tabCfg={tabCfg} tabChange={onTabChange} />}
        <CMPT_Sort sortBy={sortBy} onChange={(item) => setSortBy(item)} isShowVolume={isShowVolume} setIsShowVolume={setIsShowVolume} />

        <CMPT_Container keyword={keyword} tabCfg={tabCfg} sortBy={sortBy} isShowVolume={isShowVolume} onMarketSelect={onMarketSelect} />
      </div>
    </div>
  );
};

export default observer(Main);
